import * as Sentry from "@sentry/react";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useAuth,
  useClerk,
} from "@clerk/clerk-react";
import { useEffect, useState, type ReactNode } from "react";
import {
  getCurrentScopes,
  getLexiunToken,
  setClerkTokenResolver,
} from "@/lib/lexiun-jwt";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthRecoveryBanner } from "./components/shared/AuthRecoveryBanner";
import { Header } from "./components/layout/Header";
import { SentryFallback } from "./components/SentryFallback";
import { Sidebar } from "./components/layout/Sidebar";
import { GatewayProvider } from "./hooks/useGatewayRpc";
import { PlatformApiProvider } from "./hooks/usePlatformApi";
import { RuntimeContextProvider, useRuntimeContext } from "./hooks/useRuntimeContext";
import { resolveAppUrl } from "./lib/url";
import { CaseRegistry } from "./pages/CaseRegistry";
import { ClientAssignments } from "./pages/ClientAssignments";
import { Clients } from "./pages/Clients";
import { PlatformPrompts } from "./pages/PlatformPrompts";
import { ErrorDashboard } from "./pages/ErrorDashboard";
import { ActivityBrowser } from "./pages/ActivityBrowser";
import { FeedbackBrowser } from "./pages/FeedbackBrowser";
import { LlmProviders } from "./pages/LlmProviders";
import { MatterAssignments } from "./pages/MatterAssignments";
import { Matters } from "./pages/Matters";
import { Organizations } from "./pages/Organizations";
import { OperationalLogs } from "./pages/OperationalLogs";
import { OrgUsers } from "./pages/OrgUsers";
import { LlmCalls } from "./pages/LlmCalls";
import { PlatformHealth } from "./pages/PlatformHealth";
import { PlatformSettings } from "./pages/PlatformSettings";
import { PlatformExtensions } from "./pages/PlatformExtensions";
import { RuntimeFleet } from "./pages/RuntimeFleet";
import { ExecutionLedger } from "./pages/ExecutionLedger";
import { Checkpoints } from "./pages/Checkpoints";
import { RuntimeParameters } from "./pages/RuntimeParameters";
import { UserRuntimePaneRoute } from "./pages/UserRuntimePane";
import { RuntimeOverview } from "./pages/RuntimeOverview";
import { Specialists } from "./pages/Specialists";
import { RUNTIME_FLEET_PATH, RUNTIME_PAGE_PATH, SPECIALISTS_PATH } from "@/lib/routes";
import { SignInPage } from "./pages/SignInPage";
import { SignUpPage } from "./pages/SignUpPage";
import { SupervisorApprovals } from "./pages/SupervisorApprovals";
import { SupervisorMonitor } from "./pages/SupervisorMonitor";
import { SupervisorOverview } from "./pages/SupervisorOverview";
import { Tasks } from "./pages/Tasks";

// Vite sets BASE_URL to "/portal/" in production builds and "/" in dev.
// Strip trailing slash to get the basename React Router expects.
const basename = import.meta.env.BASE_URL.replace(/\/+$/, "") || "/";

const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

/**
 * Inner wrapper: RuntimeContext reads wsUrl/token and passes to GatewayProvider.
 * Must be INSIDE PlatformApiProvider (RuntimeContext uses usePlatformApi).
 * Must be OUTSIDE GatewayProvider (controls the wsUrl prop).
 *
 * Post-2026-04-30 auth-flow pivot: GatewayProvider no longer needs a
 * Clerk-token getter — it picks up the cached Lexiun JWT via the shared
 * `getLexiunToken` helper.
 */
function RuntimeGatewayBridge({ children }: { children: React.ReactNode }) {
  const { gatewayWsUrl, gatewayToken } = useRuntimeContext();
  return (
    <GatewayProvider wsUrl={gatewayWsUrl} token={gatewayToken ?? undefined}>
      {children}
    </GatewayProvider>
  );
}

/** Sync Clerk identity → Sentry user scope. Must be mounted inside
 *  <ClerkProvider>. See ai-docs/sentry-integration-guide.md §7/§8. */
function SentryUserSync() {
  const { userId, orgId } = useAuth();
  useEffect(() => {
    if (userId) {
      Sentry.setUser({ id: userId });
      if (orgId) Sentry.setTag("org_id", orgId);
    } else {
      Sentry.setUser(null);
    }
  }, [userId, orgId]);
  return null;
}

/**
 * Bridges Clerk's `useAuth().getToken()` into the module-level
 * `setClerkTokenResolver` so non-React modules (lexiun-jwt.ts,
 * platform-api, gateway-client) can reach the Clerk JWT synchronously.
 * Mounted inside ClerkProvider; renders nothing.
 */
function ClerkTokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setClerkTokenResolver(async () => (await getToken()) ?? null);
    return () => setClerkTokenResolver(null);
  }, [getToken]);
  return null;
}

/**
 * Route-level platform-admin gate.
 *
 * LexPortal is supposed to be PLATFORM_ADMIN-only, but Clerk's
 * cross-subdomain SSO (`clerk.lexiun.ai` is shared with LexIDE on
 * `app.lexiun.ai`) means ANY authenticated Clerk session validates here
 * — including ORG_ADMINs and MEMBERs of customer orgs. Per-endpoint
 * backend RBAC blocks data reads, but the SPA shell still renders, which
 * leaks platform-section topology + advertises action buttons that
 * shouldn't be visible. This gate fences the shell at the route level by
 * inspecting the `scopes` claim minted by lexserver's
 * `/api/auth/exchange` and refusing to render `<AppLayout>` for sessions
 * without `platform.admin`.
 *
 * UX: when the scope check fails, the gate silently signs the user
 * out (Clerk SSO cookie is shared with app.lexiun.ai — sign-out is
 * global by design here) and lands them on `/sign-in` so they can
 * authenticate as a platform-admin identity. No intermediate warning
 * page: the user already typed a URL or followed a link, the right
 * action is to take them straight to where they can type the right
 * credentials.
 *
 * See ai-docs/lexportal-clerk-gate-non-platform-admin-handoff.md.
 */
/**
 * ⚠ BINDING RULE FOR THIS GATE — "could not ask" is NOT "no".
 *
 * This gate's denial is `clerk.signOut()`, and because the Clerk SSO cookie is
 * shared with app.lexiun.ai that sign-out is GLOBAL: it logs the same human out
 * of LexIDE too, mid-turn, for a reason that has nothing to do with them. That
 * makes it effectively irreversible, so it may fire ONLY on a POSITIVE denial —
 * a token we actually obtained whose scopes lack `platform.admin`.
 *
 * The trap this replaces (found by `go-live` 2026-09-01, reproduced live at
 * 05:15Z when both prod sessions died together minutes after a deploy swap):
 * `lexiun-jwt.ts` never throws by design — `getLexiunToken()` returns `null` on
 * !res.ok and on a network error, and `getCurrentScopes()` is documented to
 * return `[]` "rather than throwing". So the catch block was never the path.
 * A transient `/api/auth/exchange` failure ran the MAIN path with `scopes === []`,
 * and `[].includes("platform.admin")` is false → global sign-out. Prod-only,
 * because prod slot-swaps lexserver behind the network while localdev is
 * 127.0.0.1 and never flaps — which is why it looked mysterious.
 *
 * Returning `[]`-not-throwing is CORRECT for a rendering decision ("default to
 * no permissions"). It is unsafe for a caller whose action destroys a session.
 * The fix is not to change `lexiun-jwt.ts` for everyone — it is for THIS caller
 * to stop spelling "unreachable" the same way as "denied".
 */
// Exported ONLY so the sign-out invariant below is testable: a gate whose
// denial is a GLOBAL sign-out must be pinned by a test that fails if
// "unreachable" ever starts signing out again. Not intended for reuse.
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  // THREE states, not two. "unreachable" is the state the old code could not
  // represent, which is exactly why it acted as though it were a denial.
  const [status, setStatus] = useState<"loading" | "ok" | "unreachable">("loading");
  const [attempt, setAttempt] = useState(0);
  const clerk = useClerk();
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Force the exchange (idempotent — cached after first call) so scopes are
      // populated even if no other consumer has run yet. A null return means the
      // exchange did not happen; it does NOT mean the caller was refused.
      let token: string | null = null;
      try {
        token = await getLexiunToken();
      } catch {
        // Defensive: lexiun-jwt is documented not to throw, but a caller whose
        // failure mode is a global sign-out does not rely on that promise.
        token = null;
      }
      if (cancelled) return;
      if (token === null) {
        // COULD NOT ASK. Never sign out here — that is the defect. Retry a few
        // times with backoff to ride out a slot swap (the common case, which
        // self-heals in seconds), then surface something actionable.
        if (attempt < 3) {
          const delayMs = 1000 * 2 ** attempt; // 1s, 2s, 4s
          setTimeout(() => {
            if (!cancelled) setAttempt((n) => n + 1);
          }, delayMs);
          return;
        }
        setStatus("unreachable");
        return;
      }
      let scopes: string[] = [];
      try {
        scopes = await getCurrentScopes();
      } catch {
        scopes = [];
      }
      if (cancelled) return;
      if (!scopes.includes("platform.admin")) {
        // POSITIVE DENIAL — we reached the server, it answered, and the answer
        // does not carry platform.admin. Only here is the silent kick correct:
        // global sign-out (clears the shared Clerk cookie) + land on /sign-in.
        // The fallback below keeps the UI from flashing the platform shell
        // while the sign-out resolves.
        await clerk.signOut({ redirectUrl: resolveAppUrl("/sign-in") });
        return;
      }
      setStatus("ok");
    })();
    return () => {
      cancelled = true;
    };
  }, [clerk, attempt]);
  if (status === "unreachable") {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-canvas text-foreground">
        <p className="text-sm">Could not verify your access right now.</p>
        <p className="max-w-md text-center text-xs opacity-70">
          The platform server did not answer. You are still signed in — this is
          not a permissions problem. It usually clears within a few seconds of a
          deploy.
        </p>
        <button
          type="button"
          className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          onClick={() => {
            setStatus("loading");
            setAttempt((n) => n + 1);
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas text-foreground">
        <p className="text-sm opacity-70">Loading…</p>
      </div>
    );
  }
  return <>{children}</>;
}

/** Provider tree: PlatformApi → RuntimeContext → Gateway → App */
function ClerkProviderStack({ children }: { children: React.ReactNode }) {
  // ClerkTokenBridge registers the module-level Clerk-token resolver so
  // non-React modules (lib/lexiun-jwt.ts, platform-api, gateway-client)
  // can reach the Clerk JWT. PlatformApi + Gateway both consume the
  // resolved Lexiun JWT via getLexiunToken — no need to thread the Clerk
  // token through props anymore (post-2026-04-30 auth-flow pivot).
  return (
    <>
      <ClerkTokenBridge />
      <SentryUserSync />
      <PlatformApiProvider>
        <RuntimeContextProvider>
          <RuntimeGatewayBridge>{children}</RuntimeGatewayBridge>
        </RuntimeContextProvider>
      </PlatformApiProvider>
    </>
  );
}

/** Provider tree for local dev (no Clerk). */
function PlainProviderStack({ children }: { children: React.ReactNode }) {
  return (
    <PlatformApiProvider>
      <RuntimeContextProvider>
        <RuntimeGatewayBridge>
          {children}
        </RuntimeGatewayBridge>
      </RuntimeContextProvider>
    </PlatformApiProvider>
  );
}

function AppLayout() {
  return (
    <div className="flex h-screen bg-canvas text-foreground">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <main className="cd-grid flex-1 overflow-auto p-6">
          <AuthRecoveryBanner />
          <Routes>
            {/* Default landing: Organizations is the platform-admin home. */}
            <Route path="/" element={<Navigate to="/orgs" replace />} />
            {/* Runtime monitoring (tabbed — all per-runtime features in one page) */}
            <Route path={RUNTIME_PAGE_PATH} element={<RuntimeOverview />} />

            {/* Platform management pages */}
            <Route path="/orgs" element={<Organizations />} />
            <Route
              path="/orgs/:orgId/clients/:clientId/matters/:matterId/assignments"
              element={<MatterAssignments />}
            />
            <Route
              path="/orgs/:orgId/clients/:clientId/matters"
              element={<Matters />}
            />
            <Route
              path="/orgs/:orgId/clients/:clientId/assignments"
              element={<ClientAssignments />}
            />
            <Route path="/orgs/:orgId/clients" element={<Clients />} />
            <Route path="/orgs/:orgId/*" element={<OrgUsers />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/cases" element={<CaseRegistry />} />
            <Route path="/feedback" element={<FeedbackBrowser />} />
            <Route path="/feedback/:feedbackId" element={<FeedbackBrowser />} />
            <Route path="/activity" element={<ActivityBrowser />} />
            <Route path="/activity/:eventId" element={<ActivityBrowser />} />
            <Route path="/errors" element={<ErrorDashboard />} />
            <Route path="/operational-logs" element={<OperationalLogs />} />
            <Route path="/operational-logs/:logId" element={<OperationalLogs />} />
            <Route path="/observability/llm-calls" element={<LlmCalls />} />
            <Route path="/llm-providers" element={<LlmProviders />} />
            <Route path="/llm-providers/:orgId" element={<LlmProviders />} />
            <Route path="/platform-prompts" element={<PlatformPrompts />} />
            <Route path="/health" element={<PlatformHealth />} />
            <Route path="/platform/settings" element={<PlatformSettings />} />
            <Route path="/platform/extensions" element={<PlatformExtensions />} />
            <Route path={RUNTIME_FLEET_PATH} element={<RuntimeFleet />} />
            <Route path={SPECIALISTS_PATH} element={<Specialists />} />
            {/* Supervisor (lexsup) — landing Monitor (health + launchpad) +
                the runs/findings Overview drill-down + the L6 approval queue. */}
            <Route path="/supervisor" element={<SupervisorMonitor />} />
            <Route path="/supervisor/overview" element={<SupervisorOverview />} />
            <Route path="/supervisor/approvals" element={<SupervisorApprovals />} />
            {/* Phase 21 — cross-org PLATFORM_ADMIN console for per-user
                lexruntime OpenClaw settings. Catch-all sub-route hands
                the userId to RuntimeParameters which routes to either
                the list view or the UserRuntimePane. */}
            <Route path="/runtime-parameters" element={<RuntimeParameters />} />
            <Route
              path="/runtime-parameters/:userId"
              element={<UserRuntimePaneRoute />}
            />
            {/* Centralized execution-ledger monitor (every mission +
                harness terminal run across all orgs). Both routes mount
                the same page; :executionId drives URL-bound selection
                of the detail pane. Handoff: ai-docs/handoffs/
                execution-ledger-lexportal-monitoring-handoff-2026-06-22.md */}
            <Route path="/execution-ledger" element={<ExecutionLedger />} />
            <Route
              path="/execution-ledger/:executionId"
              element={<ExecutionLedger />}
            />
            {/* Checkpoints — a checkpoint-scoped lens on the same ledger
                (kind:harness + harnessId==="case-checkpoint"). Verifies
                Lexiun Git is firing per-case/fleet. Handoff: ai-docs/
                handoffs/lexiun-git-monitor-lexportal-handoff-2026-07-24.md */}
            <Route path="/checkpoints" element={<Checkpoints />} />
            <Route
              path="/checkpoints/:executionId"
              element={<Checkpoints />}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function AppInner() {
  if (clerkPk) {
    return (
      <ClerkProvider
        publishableKey={clerkPk}
        signInUrl={resolveAppUrl("/sign-in")}
        signUpUrl={resolveAppUrl("/sign-up")}
        signInFallbackRedirectUrl={resolveAppUrl("/")}
        signUpFallbackRedirectUrl={resolveAppUrl("/")}
        afterSignOutUrl={resolveAppUrl("/sign-in")}
      >
        {/*
          signInFallbackRedirectUrl + signUpFallbackRedirectUrl ensure
          Clerk sends UI-driven sign-ins back to `/` after auth. These
          DO NOT affect `@clerk/testing`'s `clerk.signIn()` helper —
          that helper creates a session programmatically and leaves
          the browser where it was, so tests must explicitly
          `page.goto("/")` after calling it.
        */}
        <BrowserRouter basename={basename}>
          <Routes>
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            <Route
              path="/*"
              element={
                <>
                  <SignedIn>
                    <ClerkProviderStack>
                      <RequirePlatformAdmin>
                        <AppLayout />
                      </RequirePlatformAdmin>
                    </ClerkProviderStack>
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
          </Routes>
        </BrowserRouter>
      </ClerkProvider>
    );
  }

  return (
    <BrowserRouter basename={basename}>
      <PlainProviderStack>
        <AppLayout />
      </PlainProviderStack>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <AppInner />
    </Sentry.ErrorBoundary>
  );
}
